import React, { useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  createReferralPartner,
  fetchReferralPartners,
} from "../features/admin/slice-extensions/admin-slice-extension";
import {
  selectReferralPartners,
  selectReferralPartnersStatus,
  selectReferralPartnersTotalCount,
  selectReferralPartnersTotalPages,
} from "../features/admin/admin-slice";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import AdminTabs from "../components/admin/AdminTabs";
import ReferralPartnerRow from "../components/admin/ReferralPartnerRow";
import CreatePartnerModal from "../components/admin/CreatePartnerModal";

const PAGE_SIZE = 20;

/** The referral partner overview: who refers, what accrued, who to pay. */
const AdminReferralsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const partners = useAppSelector(selectReferralPartners);
  const status = useAppSelector(selectReferralPartnersStatus);
  const totalCount = useAppSelector(selectReferralPartnersTotalCount);
  const totalPages = useAppSelector(selectReferralPartnersTotalPages);

  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);

  useEffect(() => {
    dispatch(fetchReferralPartners({ page, pageSize: PAGE_SIZE }));
  }, [dispatch, page]);

  const create = async (name: string, email: string, notes: string) => {
    setCreateBusy(true);
    try {
      await dispatch(
        createReferralPartner({ name, email, notes: notes || undefined })
      ).unwrap();
      toast.success("Partner created — open them to copy the terms link.");
      setCreating(false);
    } catch (error) {
      toast.error((error as { message?: string })?.message || "Failed to create the partner");
    } finally {
      setCreateBusy(false);
    }
  };

  const isLoading = status === LoadingStatus.Loading;

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
          <div className="lg:max-w-[1280px]">
            <Breadcrumbs />
            <div className="space-y-4 py-6 lg:space-y-6">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h1>{t("admin.title")}</h1>
                  <p className="text-grey-40 dark:text-grey-70">{t("admin.subtitle")}</p>
                </div>
                <button
                  type="button"
                  className="btn btn-primary flex items-center gap-1.5"
                  onClick={() => setCreating(true)}
                >
                  <UserPlus size={16} />
                  New partner
                </button>
              </div>

              <AdminTabs />

              {status === LoadingStatus.Failed && (
                <div className="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  <p>Failed to load referral partners.</p>
                </div>
              )}

              {isLoading && partners.length === 0 ? (
                <p className="py-10 text-center text-grey-40 dark:text-grey-70">
                  {t("admin.loading")}
                </p>
              ) : partners.length === 0 && status !== LoadingStatus.Failed ? (
                <div className="with-border rounded-2xl p-8 text-center text-grey-40 dark:text-grey-70">
                  No referral partners yet — create the first one.
                </div>
              ) : (
                <>
                  <p className="text-sm text-grey-50 dark:text-grey-60">
                    {totalCount} partner{totalCount === 1 ? "" : "s"}
                  </p>
                  <div className="space-y-2 lg:space-y-4">
                    {partners.map((partner) => (
                      <ReferralPartnerRow key={partner.id} partner={partner} />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-2">
                      <button
                        className="btn btn-secondary"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        {t("admin.previous")}
                      </button>
                      <span className="text-sm text-grey-50 dark:text-grey-60">
                        {t("admin.pageOf", { page, totalPages })}
                      </span>
                      <button
                        className="btn btn-secondary"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        {t("admin.next")}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {creating && (
        <CreatePartnerModal
          busy={createBusy}
          onCreate={create}
          onClose={() => setCreating(false)}
        />
      )}
    </section>
  );
};

export default AdminReferralsPage;
